<template>
  <button
    type="button"
    class="issue-row"
    :aria-label="ariaLabel"
    @click="$emit('select', row)"
  >
    <span class="issue-row__thumb" :class="`issue-row__thumb--${row.pillVariant}`">
      <img
        v-if="row.avatarUrl"
        :src="row.avatarUrl"
        :alt="row.name"
        class="issue-row__thumb-img"
        @error="onThumbError"
      />
      <f7-icon
        v-else
        :f7="reasonIcon(row.reason)"
        size="20"
        class="issue-row__thumb-glyph"
      ></f7-icon>
    </span>
    <span class="issue-row__body">
      <!-- THE QUESTION LEADS. dir="auto" — it is the leader's raw text and may
           be wholly Latin; without it the closing punctuation jumps to the wrong
           end of an RTL line. Clamped to two lines here (and ONLY here): the row
           is a list entry, and the full question is one tap away on the card,
           which never clamps it. -->
      <span v-if="row.question" class="issue-row__question" dir="auto">{{ row.question }}</span>
      <!-- Falls back to the item name for a row whose question we don't have —
           a legacy issue raised before the redesign. Never leaves the row
           headless. -->
      <span v-else class="issue-row__name">{{ row.name }}</span>
      <span class="issue-row__meta">
        <!-- The reason is DEMOTED to a chip: it is a reporting tag, subordinate
             to the question above it. -->
        <span class="issue-row__reason">{{ reasonLabel }}</span>
        <span v-if="boxLabel" class="issue-row__box">{{ boxText }}</span>
        <span class="issue-row__meta-sep" aria-hidden="true">·</span>
        <time class="issue-row__time" :datetime="row.createdAt || undefined">{{ relativeTime }}</time>
      </span>
      <span
        v-if="row.outcomeKey"
        class="issue-row__outcome"
        :class="{ 'issue-row__outcome--neutral': row.outcomeIsNeutral }"
      >
        <f7-icon
          v-if="!row.outcomeIsNeutral"
          f7="checkmark_circle_fill"
          size="13"
        ></f7-icon>
        {{ t(row.outcomeKey) }}
      </span>
      <turn-pill v-else :variant="row.pillVariant" :announce="false"></turn-pill>
    </span>
    <f7-icon f7="chevron_left" size="18" class="issue-row__chev"></f7-icon>
  </button>
</template>

<script setup>
/**
 * IssueRow — one issue as a tappable list row, shared by BOTH lists on the hub:
 * the actionable "waiting on you" list and the past-decisions history that
 * interaction-v2's index route made possible. Sharing the row is what keeps a
 * single issue looking the same in either list — only its status fragment
 * differs (a pill while unresolved, WHAT HAPPENED once there's a terminal
 * outcome, per `row.outcomeKey` — see terminalOutcomeKey).
 *
 * The whole row is the tap target, so the pill/outcome is a plain display span
 * (`announce="false"` on the pill): the button already carries the accessible
 * name (extended here with the reason, box, time, and status), and a nested
 * interactive element or live region inside it would double up.
 */
import { useRepo } from 'pinia-orm';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import TurnPill from '@/components/collection/TurnPill.vue';
import { pillLabelKey, reasonIcon, reasonLabelKey } from '@/constants/collectionIssue.js';
import OrderBox from '@/models/commerce/OrderBox.js';

const props = defineProps({
  // A row from useDecisionsInbox: { issueId, orderBoxId, reason, status, name,
  // avatarUrl, pillVariant, createdAt, outcomeKey, outcomeIsNeutral, ... }.
  row: {
    type: Object,
    required: true,
  },
});

defineEmits(['select']);

const { t, locale } = useI18n({ useScope: 'global' });

function onThumbError(event) {
  event.target.style.display = 'none';
}

const reasonLabel = computed(() => t(reasonLabelKey(props.row.reason)));

// Read off the already-normalized pinia-orm store (no extra fetch — the box is
// already loaded whenever the member reached their hub through orders/box
// browsing; a cold notification deep-link may not have it, and the fragment is
// simply omitted then). Mirrors IssueDecisionPage's identical lookup for the
// order-line strip.
const orderBoxRepo = useRepo(OrderBox);
const boxLabel = computed(() => {
  const id = props.row.orderBoxId;
  if (id == null) return null;
  const box = orderBoxRepo.find(String(id));
  return box?.title || box?.code || null;
});
const boxText = computed(() => (boxLabel.value ? t('collection.orderLine.box', { box: boxLabel.value }) : ''));

// ui-western-digits lock: `-u-nu-latn` forces Latin numerals so the Arabic
// locale never renders Arabic-Indic digits in a timestamp — mirrors
// IssueTimeline's identical formatter lock.
const relativeTimeFormatter = computed(
  () =>
    new Intl.RelativeTimeFormat(locale.value === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB', {
      numeric: 'auto',
    }),
);

const RELATIVE_UNITS = [
  ['year', 31536000],
  ['month', 2592000],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
];

const relativeTime = computed(() => {
  if (!props.row.createdAt) return '';
  const date = new Date(props.row.createdAt);
  if (Number.isNaN(date.getTime())) return '';

  const diffSec = (date.getTime() - Date.now()) / 1000;
  const absSec = Math.abs(diffSec);
  const [unit, secondsInUnit] = RELATIVE_UNITS.find(([, s]) => absSec >= s || s === 1);
  const value = Math.round(diffSec / secondsInUnit);
  return relativeTimeFormatter.value.format(value, unit);
});

const statusText = computed(() =>
  props.row.outcomeKey ? t(props.row.outcomeKey) : t(pillLabelKey(props.row.pillVariant)),
);

// The question joins the accessible name, ahead of the reporting tag — a screen
// reader user hears what she is being asked, not just how it was categorised.
// The item name stays too: the question rarely repeats it, and without it the
// row would not say which item this is about.
const ariaLabel = computed(() =>
  [
    props.row.name,
    props.row.question,
    reasonLabel.value,
    boxText.value,
    relativeTime.value,
    statusText.value,
  ]
    .filter(Boolean)
    .join('، '),
);
</script>

<style scoped>
.issue-row {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  width: 100%;
  min-height: 64px;
  padding: 11px 12px;
  /* --ci-control-border, not --ci-border: the whole row IS the control, and
     --ci-border measures 1.15–1.24:1 against its surface — a hairline divider,
     not a control edge (WCAG 1.4.11 wants 3:1). */
  border: 1px solid var(--ci-control-border);
  border-radius: var(--poolit-radius-lg);
  background: var(--ci-surface);
  text-align: start;
  cursor: pointer;
  transition: background var(--poolit-transition-fast, 150ms) ease;
}

.issue-row:active {
  background: var(--ci-surface-soft, #eef2f0);
}

.issue-row:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--ci-brand-ring, rgba(142, 91, 254, 0.4));
}

.issue-row__thumb {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: var(--poolit-radius-md);
  overflow: hidden;
  display: grid;
  place-items: center;
}

/* The fallback glyph's tint tracks whose court the issue is in, matching the
   row's pill. A real product photo covers it, so this only shows for items
   without one. */
.issue-row__thumb--member {
  background: var(--ci-await-bg, #fbefd4);
  color: var(--ci-await-fg, #b45309);
}

.issue-row__thumb--handled {
  background: var(--ci-brand-bg, #f0eaff);
  color: var(--ci-brand-fg, #6621e2);
}

.issue-row__thumb--resolved {
  background: var(--ci-ok-bg, #e1f3e8);
  color: var(--ci-ok-fg, #16964f);
}

.issue-row__thumb--oos {
  background: var(--ci-muted-bg, #eceef1);
  color: var(--ci-muted-fg, #374151);
}

.issue-row__thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.issue-row__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: flex-start;
}

/* The headline. Two lines, then ellipsis — the card behind the tap carries it
   in full and never clamps. */
.issue-row__question {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 100%;
  font-size: var(--poolit-font-size-md);
  font-weight: var(--poolit-font-weight-semibold);
  line-height: 1.4;
  color: var(--poolit-text-primary);
}

/* Legacy fallback headline — a pre-redesign issue with no question. */
.issue-row__name {
  font-weight: var(--poolit-font-weight-semibold);
  font-size: var(--poolit-font-size-md);
  color: var(--poolit-text-primary);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Below the headline: the reason chip, the box, and the time. */
.issue-row__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 100%;
  font-size: var(--poolit-font-size-xs);
  color: var(--poolit-text-muted);
}

/* Subordinate by design — a reporting tag, not the headline it used to be. */
.issue-row__reason {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: var(--poolit-radius-full);
  background: var(--ci-surface-soft);
  font-size: var(--poolit-font-size-2xs);
  white-space: nowrap;
}

.issue-row__box {
  flex-shrink: 0;
  white-space: nowrap;
}

.issue-row__meta-sep {
  flex-shrink: 0;
}

.issue-row__time {
  flex-shrink: 0;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* What actually happened, replacing the pill on a terminal history row. */
.issue-row__outcome {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: var(--poolit-font-size-xs);
  font-weight: var(--poolit-font-weight-semibold);
  color: var(--ci-ok-fg);
}

/* A withdrawal (`request-closed`) is terminal but not a win — no checkmark, no
   success green, matching IssueTimeline's neutral-terminal treatment. */
.issue-row__outcome--neutral {
  color: var(--poolit-text-muted, #66746d);
}

.issue-row__chev {
  flex-shrink: 0;
  align-self: center;
  color: var(--poolit-text-placeholder, #9ca3af);
}
</style>
